/**
 * KaTeX Text Components
 * Ported from reference_legacy/quebon-lms-web/components/lesson/quiz-types/shared/katex-text.tsx
 */

import { useEffect, useRef, useMemo } from 'react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { cn } from '../../../lib/utils'

interface KaTeXProps {
  math: string
  className?: string
  displayMode?: boolean
}

/**
 * KaTeX 수식 렌더링 컴포넌트
 */
export function KaTeX({ math, className, displayMode = false }: KaTeXProps) {
  const containerRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (containerRef.current && math) {
      try {
        katex.render(math, containerRef.current, {
          throwOnError: false,
          displayMode,
        })
      } catch (error) {
        console.error('KaTeX render error:', error)
        if (containerRef.current) {
          containerRef.current.textContent = math
        }
      }
    }
  }, [math, displayMode])

  return <span ref={containerRef} className={cn('katex-text', className)} />
}

interface KatexTextProps {
  text: string
  className?: string
  imgs?: string[]
}

/**
 * 텍스트 이스케이프 처리
 */
function escapeText(t: string): string {
  return t
    .replace(/＝/g, '=')  // full-width equal sign
    .replace(/－/g, '-')  // full-width hyphen-minus
    .replace(/＋/g, '+')  // full-width plus sign
    .replace(/𝑚/g, 'm')  // Greek small letter m
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
}

/**
 * 줄바꿈을 포함한 텍스트 렌더링
 */
function TextWithLineBreaks({ text }: { text: string }) {
  const lines = text.split('\n')
  
  return (
    <>
      {lines.map((line, idx) => (
        <span key={idx}>
          {line}
          {idx < lines.length - 1 && <br />}
        </span>
      ))}
    </>
  )
}

/**
 * KaTeX 수식이 포함된 텍스트를 렌더링하는 컴포넌트
 * $$...$$ 패턴을 수식으로 변환
 */
export function KatexText({ text, className }: KatexTextProps) {
  const rendered = useMemo(() => {
    if (!text) return null

    const _t = text.normalize ? text.normalize('NFC') : text
    const katexReg = /\$\$([^$]+)\$\$/gim
    const parts: Array<{ type: 'text' | 'math'; content: string; key: string }> = []
    
    let lastIndex = 0
    let match: RegExpExecArray | null
    let idx = 0

    while ((match = katexReg.exec(_t)) !== null) {
      // 수식 이전 텍스트
      if (match.index > lastIndex) {
        const textContent = _t.slice(lastIndex, match.index)
        parts.push({
          type: 'text',
          content: escapeText(textContent),
          key: `text-${idx++}`,
        })
      }
      // 수식
      parts.push({
        type: 'math',
        content: match[1],
        key: `math-${idx++}`,
      })
      lastIndex = match.index + match[0].length
    }

    // 남은 텍스트
    if (lastIndex < _t.length) {
      parts.push({
        type: 'text',
        content: escapeText(_t.slice(lastIndex)),
        key: `text-${idx++}`,
      })
    }

    return parts
  }, [text])

  if (!rendered || rendered.length === 0) {
    return <span className={className}>{text}</span>
  }

  return (
    <span className={cn('leading-relaxed', className)}>
      {rendered.map((part) => {
        if (part.type === 'math') {
          return <KaTeX key={part.key} math={part.content} />
        }
        return <TextWithLineBreaks key={part.key} text={part.content} />
      })}
    </span>
  )
}
